import React from 'react'
import { MapContext, MapType } from './mapContext'
import GoogleMap from '../googleMap/map'
import MapboxMap from '../mapbox/map'
import { LatLngBoundsLiteral, LatLngLiteral } from './mapPosition'

export type MapMouseEvent =
  | google.maps.MapMouseEvent
  | (mapboxgl.MapMouseEvent & mapboxgl.EventData)

export type MapGeneralProps = {
  mapProvider?: 'google' | 'mapbox'
  tilt?: number
  heading?: number
  mapType?: MapType
  children?: React.ReactNode
  is3D?: boolean
  styleProps?: {
    [key: string]: string
  }
  onClick?: (event: MapMouseEvent) => void
  onContextMenu?: (event: MapMouseEvent) => void
  onMouseUp?: (event: MapMouseEvent) => void
  onMouseDown?: (event: MapMouseEvent) => void
  onMouseMove?: (event: MapMouseEvent) => void
  onMouseOut?: (event: MapMouseEvent) => void
  onMouseOver?: (event: MapMouseEvent) => void
  onDrag?: (event: MapMouseEvent) => void
  onDragEnd?: (event: MapMouseEvent) => void
  onDragStart?: (event: MapMouseEvent) => void
  onZoomChanged?: (newZoom: number | undefined) => void
}

export type MapProps = (
  | {
      bounds?: never
      center: LatLngLiteral
      zoom: number
    }
  | {
      bounds: LatLngBoundsLiteral
      center?: never
      zoom?: never
    }
) &
  MapGeneralProps

/**
 * Componente de mapa com várias propriedades configuráveis.
 *
 * @param {Object} props - Propriedades do componente.
 * @param {Object} [props.bounds] - Limites do mapa.
 * @param {Object} [props.center] - Coordenadas do centro do mapa.
 * @param {number} [props.zoom] - Nível de zoom do mapa.
 * @param {number} [props.tilt=0] - Inclinação do mapa. Default: `0`.
 * @param {number} [props.heading=0] - Direção do mapa. Default: `0`.
 * @param {string} [props.mapType='roadmap'] - Tipo de mapa. Default: `'roadmap'`.
 * @param {boolean} [props.is3D=false] - Habilita visualização 3D. Default: `false`.
 * @param {React.ReactNode} [props.children] - Componentes filhos (camadas).
 * @param {function} [props.onClick] - Função chamada no evento de clique.
 * @param {function} [props.onContextMenu] - Função chamada no evento de clique com botão direito.
 * @param {function} [props.onMouseUp] - Função chamada no evento de soltar o clique.
 * @param {function} [props.onMouseDown] - Função chamada no evento de clicar.
 * @param {function} [props.onMouseMove] - Função chamada no evento de mover o mouse.
 * @param {function} [props.onMouseOut] - Função chamada no evento de sair do mapa.
 * @param {function} [props.onMouseOver] - Função chamada no evento de passar o mouse.
 * @param {function} [props.onDrag] - Função chamada no evento de arrastar.
 * @param {function} [props.onDragEnd] - Função chamada no evento de soltar o arraste.
 * @param {function} [props.onDragStart] - Função chamada no evento de iniciar o arraste.
 * @param {function} [props.onZoomChanged] - Função chamada no evento de mudança de estado do zoom.
 */
export default function Map({
  mapProvider = 'google',
  mapType = 'roadmap',
  is3D = false,
  ...otherProps
}: MapProps) {
  let mapJSXElement: JSX.Element
  if (mapProvider === 'google') {
    mapJSXElement = <GoogleMap mapType={mapType} is3D={is3D} {...otherProps} />
  } else if (mapProvider === 'mapbox') {
    mapJSXElement = <MapboxMap mapType={mapType} is3D={is3D} {...otherProps} />
  } else {
    throw new Error('Invalid map provider')
  }

  return (
    <MapContext.Provider value={{ mapProvider, mapType, is3D }}>
      {mapJSXElement}
    </MapContext.Provider>
  )
}

export const attributionMarginRightPx = 28
export const attributionMarginLeftPx = 28
